import React from "react";
import { MdDragIndicator } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { HiOutlineMusicalNote, HiOutlineTv, HiOutlinePlus, HiOutlineEye, HiOutlineTrash } from "react-icons/hi2";

const ItemTypes = { SONG: "song" };

const SongArtworkCell: React.FC<{ artworkURL?: string; title: string }> = ({ artworkURL, title }) => {
  const [imgError, setImgError] = React.useState(false);
  const showPlaceholder = !artworkURL || imgError;
  if (showPlaceholder) {
    return (
      <div className="flex items-center justify-center w-20 h-20 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 shrink-0">
        <HiOutlineMusicalNote className="w-10 h-10" strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img src={artworkURL} alt={title} onError={() => setImgError(true)} className="w-20 h-20 rounded-xl object-cover shrink-0 shadow-sm" />
  );
};

interface Song {
  id: string;
  title: string;
  artist: string;
  album: string;
  artworkURL?: string;
  youtubeURL?: string;
  youtubeLink?: string;
  youtubeLinks?: string[];
  favorite?: boolean;
}

interface SongListProps {
  songs: Song[];
  handleMoveSong: (dragIndex: number, hoverIndex: number) => void;
  handleRemoveSong: (song: Song) => void;
  handleAddSong: () => void;
  settingPlaylist: boolean;
  isCurrentPlaylist: boolean;
  handleSetPlaylist: () => void;
  onPresentationMode?: () => void;
  onViewSong?: (song: Song) => void;
}

const SongList: React.FC<SongListProps> = ({
  songs,
  handleMoveSong,
  handleRemoveSong,
  handleAddSong,
  onPresentationMode,
  onViewSong,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const SongItem: React.FC<{
    song: Song;
    index: number;
    moveItem: (dragIndex: number, hoverIndex: number) => void;
  }> = ({ song, index, moveItem }) => {
    const ref = React.useRef<HTMLLIElement>(null);
    const [, drop] = useDrop({
      accept: ItemTypes.SONG,
      hover(draggedItem: { index: number }) {
        if (!ref.current) return;
        const dragIndex = draggedItem.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;
        moveItem(dragIndex, hoverIndex);
        draggedItem.index = hoverIndex;
      },
    });
    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.SONG,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });
    drag(drop(ref));

    const handleView = () => {
      if (onViewSong) onViewSong(song);
      else navigate(`/songs/${song.id}`);
    };

    return (
      <li ref={ref} className={`${isDragging ? "opacity-50 scale-[0.98]" : "opacity-100"} transition-all duration-200`}>
        <div className="group flex flex-col sm:flex-row justify-between items-start gap-4 p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-md transition-all min-w-0">
          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 w-full">
            <SongArtworkCell artworkURL={song.artworkURL} title={song.title} />
            <div className="min-w-0 flex-1 overflow-hidden">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 line-clamp-2 leading-snug break-words">
                {song.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">{t("artist")}: {song.artist || "—"}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{t("album")}: {song.album || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleView}
              className="p-2.5 rounded-xl text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors"
              title={t("view")}
            >
              <HiOutlineEye className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleRemoveSong(song)}
              className="p-2.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors"
              title={t("remove")}
            >
              <HiOutlineTrash className="w-5 h-5" />
            </button>
            <div className="p-2.5 cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <MdDragIndicator size={20} />
            </div>
          </div>
        </div>
      </li>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 min-w-0">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-between gap-3 sm:gap-4 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shrink-0">
              <HiOutlineMusicalNote className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 truncate">
              {t("songs")} {songs.length > 0 && `(${songs.length})`}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              onClick={handleAddSong}
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA] text-[#5932EA] hover:bg-[#5932EA]/10 transition-all"
            >
              <HiOutlinePlus className="w-5 h-5" />
              {t("add")} {t("songs")}
            </button>
            {onPresentationMode && songs.length > 0 && (
              <button
                onClick={onPresentationMode}
                className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] shadow-sm hover:shadow transition-all"
              >
                <HiOutlineTv className="w-5 h-5" />
                {t("presentationMode")}
              </button>
            )}
          </div>
        </div>
        {songs.length === 0 ? (
          <div
            onClick={handleAddSong}
            className="flex flex-col items-center justify-center py-16 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[#5932EA]/50 hover:bg-[#5932EA]/5 hover:text-[#5932EA] cursor-pointer transition-all"
          >
            <HiOutlineMusicalNote className="w-14 h-14 mb-3 opacity-40" />
            <p className="text-sm font-medium">{t("noSongsYet") || "No songs yet"}</p>
            <p className="text-xs mt-1">{t("add")} {t("songs")}</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {songs.map((song, index) => (
              <SongItem
                key={song.id}
                index={index}
                song={song}
                moveItem={handleMoveSong}
              />
            ))}
          </ul>
        )}
      </div>
    </DndProvider>
  );
};

export default SongList;